import React, { useState } from 'react';
import { Input } from '../BossUI';
import PublicSurveyPage from './PublicSurveyPage';
import { Question, ScaleButtons, SymptomScaleGrid, HoursOfSleepChoice, YesNoToggle, SurveyTextarea } from './surveyInputs';

const todayISODate = () => new Date().toISOString().slice(0, 10);

const openDatePicker = (e) => {
  if (typeof e.target.showPicker === 'function') {
    try { e.target.showPicker(); } catch (err) { /* unsupported in this browser */ }
  }
};

const REQUIRED_FIELDS = [
  'todays_date', 'rate_instructor', 'rate_overall_experience',
  'musical_competency_rating', 'music_as_coping_skill_likelihood', 'music_for_connection_likelihood',
  'interest_in_future_groups', 'semester_outcome', 'recommend_to_veteran',
  'ptsd', 'tbi', 'depression', 'anxiety', 'prepared_to_continue_music',
  'coping_days_needed', 'coping_days_comfortable', 'coping_strategy_use_likelihood',
  'good_nights_sleep_per_week', 'hours_of_sleep', 'music_as_sleep_aid_per_week',
];

const isMissing = (values, field) => values[field] === undefined || values[field] === null || values[field] === '';

const FinalSurveyFormFields = ({ submit }) => {
  const [values, setValues] = useState({ todays_date: todayISODate() });
  const [missing, setMissing] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const set = (field, val) => setValues((prev) => ({ ...prev, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missingFields = REQUIRED_FIELDS.filter((f) => isMissing(values, f));
    if (missingFields.length) {
      setMissing(missingFields);
      return;
    }
    setMissing([]);
    setSubmitting(true);
    try {
      await submit(values);
    } finally {
      setSubmitting(false);
    }
  };

  const err = (field, msg = 'Required') => (missing.includes(field) ? msg : null);

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-xl font-bold text-neutral-900 mb-1">Warrior Music Foundation Final Survey</h2>
      <p className="text-sm text-neutral-500 mb-4">
        This survey is completed at the end of each 12 week semester and helps us determine the effectiveness of the program.
      </p>

      {missing.length > 0 && (
        <p className="mt-1 text-sm text-red-600">Please answer all required questions before submitting.</p>
      )}

      <Question label="Today's Date" error={err('todays_date')}>
        <Input
          type="date"
          value={values.todays_date || ''}
          onChange={(e) => set('todays_date', e.target.value)}
          onClick={openDatePicker}
          className="cursor-pointer"
        />
      </Question>

      <Question label="Please Rate Your Instructor" error={err('rate_instructor')}>
        <ScaleButtons min={0} max={10} value={values.rate_instructor} onChange={(v) => set('rate_instructor', v)}
          leftLabel="Unsatisfactory" rightLabel="Excellent" />
      </Question>

      <Question label="What comments do you have about your instructor?">
        <SurveyTextarea value={values.instructor_comments} onChange={(v) => set('instructor_comments', v)} />
      </Question>

      <Question label="Please Rate Your Overall Experience With WMF" error={err('rate_overall_experience')}>
        <ScaleButtons min={0} max={10} value={values.rate_overall_experience} onChange={(v) => set('rate_overall_experience', v)}
          leftLabel="Unsatisfactory" rightLabel="Excellent" />
      </Question>

      <Question label="What comments do you have about your overall experience?">
        <SurveyTextarea value={values.overall_experience_comments} onChange={(v) => set('overall_experience_comments', v)} />
      </Question>

      <Question label="Please rate your (or your child's) musical competency from your point of view" error={err('musical_competency_rating')}>
        <ScaleButtons min={0} max={10} value={values.musical_competency_rating} onChange={(v) => set('musical_competency_rating', v)}
          leftLabel="Unable to Play" rightLabel="Proficient" />
      </Question>

      <Question label="How likely are you (or your child) to use music as a coping skill in everyday life?" error={err('music_as_coping_skill_likelihood')}>
        <ScaleButtons min={0} max={10} value={values.music_as_coping_skill_likelihood} onChange={(v) => set('music_as_coping_skill_likelihood', v)}
          leftLabel="Not at all likely" rightLabel="Extremely likely" />
      </Question>

      <Question label="How likely are you (or your child) to use music as a way to meet new people and connect?" error={err('music_for_connection_likelihood')}>
        <ScaleButtons min={0} max={10} value={values.music_for_connection_likelihood} onChange={(v) => set('music_for_connection_likelihood', v)}
          leftLabel="Not at all likely" rightLabel="Extremely likely" />
      </Question>

      <Question label="Are you interested in participating with WMF musical groups or bands in the future?" error={err('interest_in_future_groups')}>
        <YesNoToggle value={values.interest_in_future_groups} onChange={(v) => set('interest_in_future_groups', v)} />
      </Question>

      <Question label="What did you get out of your semester with WMF? Did it meet your personal needs?" error={err('semester_outcome')}>
        <SurveyTextarea value={values.semester_outcome} onChange={(v) => set('semester_outcome', v)} />
      </Question>

      <Question label="Would you recommend WMF to a fellow veteran?" error={err('recommend_to_veteran')}>
        <YesNoToggle value={values.recommend_to_veteran} onChange={(v) => set('recommend_to_veteran', v)} />
      </Question>

      <Question
        label="Please rate your ability right now to cope with the symptoms of:"
        error={['ptsd', 'tbi', 'depression', 'anxiety'].some((f) => missing.includes(f)) ? 'Please rate all four' : null}
      >
        <SymptomScaleGrid values={values} onChange={set} />
      </Question>

      <Question label="Do you feel your lessons through WMF have prepared you to continue playing music in your everyday life?" error={err('prepared_to_continue_music')}>
        <YesNoToggle value={values.prepared_to_continue_music} onChange={(v) => set('prepared_to_continue_music', v)} />
      </Question>

      <Question label="Approximately how many days a week do you need to employ a coping strategy?" error={err('coping_days_needed')}>
        <ScaleButtons min={1} max={7} value={values.coping_days_needed} onChange={(v) => set('coping_days_needed', v)} />
      </Question>

      <Question label="Approximately how many days a week do you actually feel comfortable using a coping strategy?" error={err('coping_days_comfortable')}>
        <ScaleButtons min={1} max={7} value={values.coping_days_comfortable} onChange={(v) => set('coping_days_comfortable', v)} />
      </Question>

      <Question
        label="How likely are you to actually use a coping strategy you've been taught or given? (A coping strategy includes, but is not limited to deep breathing, positive imagery, safety checks, therapeutic music use, etc.)"
        error={err('coping_strategy_use_likelihood')}
      >
        <ScaleButtons min={0} max={10} value={values.coping_strategy_use_likelihood} onChange={(v) => set('coping_strategy_use_likelihood', v)}
          leftLabel="I never use them" rightLabel="I use them as a preventative measure" />
      </Question>

      <Question
        label={'On average, how many nights a week would you describe yourself as getting a "good night\'s sleep?"'}
        error={err('good_nights_sleep_per_week')}
      >
        <ScaleButtons min={1} max={7} value={values.good_nights_sleep_per_week} onChange={(v) => set('good_nights_sleep_per_week', v)} />
      </Question>

      <Question label="On average, how many hours of restful sleep do you get a night?" error={err('hours_of_sleep')}>
        <HoursOfSleepChoice value={values.hours_of_sleep} onChange={(v) => set('hours_of_sleep', v)} />
      </Question>

      <Question label="How many nights a week do you use music as a sleep aid or as a relaxation tool to aid in sleep?" error={err('music_as_sleep_aid_per_week')}>
        <ScaleButtons min={1} max={7} value={values.music_as_sleep_aid_per_week} onChange={(v) => set('music_as_sleep_aid_per_week', v)} />
      </Question>

      <div className="pt-6">
        <button
          type="submit"
          disabled={submitting}
          style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
          className="px-6 py-3 rounded-md text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </form>
  );
};

const FinalSurveyForm = () => (
  <PublicSurveyPage
    surveyType="final"
    title="Final Survey"
    renderForm={({ submit }) => <FinalSurveyFormFields submit={submit} />}
  />
);

export default FinalSurveyForm;
